import React, { useState, useEffect, useMemo } from 'react';
import {
  Check, UserPlus, Mail, Rocket, Plus, Loader2,
  Bot, ListChecks, Trash2, ArrowUp, ArrowDown, PlusCircle, Search
} from 'lucide-react';
import Swal from 'sweetalert2';
import api from '../api/axios';
import JDModal from '../components/JDModal';
import UpgradeModal from '../components/UpgradeModal';

const customStyles = `
  @keyframes blob {
    0% { transform: translate(0px, 0px) scale(1); }
    33% { transform: translate(30px, -50px) scale(1.1); }
    66% { transform: translate(-20px, 20px) scale(0.9); }
    100% { transform: translate(0px, 0px) scale(1); }
  }
  .animate-blob { animation: blob 15s infinite alternate ease-in-out; }
`;

const Schedule = () => {
  const [step, setStep] = useState(1);

  // Data state
  const [jds, setJds]                   = useState([]);
  const [remaining, setRemaining]       = useState(null); // interviews_remaining
  const [upgradeModal, setUpgradeModal] = useState(null); // 'growth' | 'scale' | null
  const [searchQuery, setSearchQuery]   = useState('');
  const [selectedJD, setSelectedJD]     = useState(null);
  const [showJDModal, setShowJDModal]   = useState(false);

  const [candidates, setCandidates]           = useState('');
  const [interviewType, setInterviewType]     = useState('Hybrid');
  const [emailBody, setEmailBody]             = useState(
    "Hi,\n\nYou have been invited to an AI-driven interview for the [Role] position.\n\nClick the link below to start:\n[Link]\n\nGood Luck!"
  );
  const [isCustom, setIsCustom]               = useState(false);
  const [customQuestions, setCustomQuestions] = useState([{ id: Date.now(), text: '', type: 'verbal' }]);
  const [isLaunching, setIsLaunching]         = useState(false);

  // ── Fetch on mount + global refresh ──────────────────────────────────────
  useEffect(() => {
    fetchJDs();
    fetchQuota();
    const handleGlobalRefresh = () => { fetchJDs(); fetchQuota(); };
    window.addEventListener('app-refresh', handleGlobalRefresh);
    return () => window.removeEventListener('app-refresh', handleGlobalRefresh);
  }, []);

  const fetchQuota = async () => {
    try {
      const res = await api.get('/me/quota');
      setRemaining(res.data.remaining);
    } catch (e) { console.error(e); }
  };

  const fetchJDs = async () => {
    try {
      const res = await api.get('/jds/');
      setJds(res.data);
    } catch (e) { console.error(e); }
  };

  // ── Filtering ─────────────────────────────────────────────────────────────
  const filteredJDs = useMemo(() => {
    return jds.filter(jd =>
      jd.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      jd.skills.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [jds, searchQuery]);

  // ── Question builder helpers ───────────────────────────────────────────────
  const handleAddQuestion    = () => setCustomQuestions([...customQuestions, { id: Date.now(), text: '', type: 'verbal' }]);
  const handleUpdateQuestion = (id, field, value) => setCustomQuestions(customQuestions.map(q => q.id === id ? { ...q, [field]: value } : q));
  const handleRemoveQuestion = (id) => setCustomQuestions(customQuestions.filter(q => q.id !== id));

  const handleMoveQuestion = (index, direction) => {
    if (index + direction < 0 || index + direction >= customQuestions.length) return;
    const newQs = [...customQuestions];
    [newQs[index], newQs[index + direction]] = [newQs[index + direction], newQs[index]];
    setCustomQuestions(newQs);
  };

  const validateStep3 = () => {
    if (isCustom) {
      if (customQuestions.length === 0) {
        Swal.fire({ icon: 'warning', title: 'Please add at least one custom question.', confirmButtonColor: '#4F46E5' });
        return;
      }
      if (customQuestions.some(q => !q.text.trim())) {
        Swal.fire({ icon: 'warning', title: 'Please fill out all question fields.', confirmButtonColor: '#4F46E5' });
        return;
      }
    }
    setStep(4);
  };

  // ── Launch ────────────────────────────────────────────────────────────────
  const handleLaunch = async () => {
    setIsLaunching(true);
    try {
      const candidateList = candidates
        .split('\n')
        .filter(email => email.includes('@'))
        .map(e => e.trim());

      if (candidateList.length === 0) {
        Swal.fire({ icon: 'warning', title: 'Please add at least one valid email address.', confirmButtonColor: '#4F46E5' });
        setIsLaunching(false);
        return;
      }

      if (remaining !== null && candidateList.length > remaining) {
        Swal.fire({
          icon: 'warning',
          title: 'Not enough interviews remaining',
          text: `You only have ${remaining} interviews remaining but are trying to invite ${candidateList.length} candidates.`,
          confirmButtonColor: '#4F46E5',
        });
        setIsLaunching(false);
        return;
      }

      const formattedQuestions = isCustom
        ? customQuestions.map((q, idx) => ({ question_text: q.text, question_type: q.type, order_index: idx }))
        : [];

      const createRes = await api.post('/campaigns/', {
        jd_id:            selectedJD.id,
        campaign_name:    `${selectedJD.title} Campaign`,
        interview_type:   interviewType,
        candidates:       candidateList,
        email_body:       emailBody,
        is_custom:        isCustom,
        custom_questions: formattedQuestions,
      });

      await api.post(`/campaigns/${createRes.data.id}/launch`, { candidate_ids: [] });

      fetchQuota(); // refresh count after launch
      // alert(`Success! Campaign launched and ${candidateList.length} invites sent.`);
      Swal.fire({
        icon: 'success',
        title: 'Campaign Launched!',
        text: `${candidateList.length} invite${candidateList.length === 1 ? '' : 's'} sent.`,
        confirmButtonColor: '#4F46E5',
      })

      // Reset wizard
      setStep(1);
      setCandidates('');
      setSelectedJD(null);
      setIsCustom(false);
      setCustomQuestions([{ id: Date.now(), text: '', type: 'verbal' }]);

    } catch (e) {
      const detail = e.response?.data?.detail;
      if (detail?.code === 'QUOTA_EXCEEDED') {
        Swal.fire({
          icon: 'warning',
          title: 'No interviews remaining',
          text: detail.message,
          confirmButtonColor: '#4F46E5',
        });
      } else {
        console.error(e);
        Swal.fire({
          icon: 'error',
          title: 'Failed to launch campaign',
          text: 'Check console for details.',
          confirmButtonColor: '#4F46E5',
        });
      }
    } finally {
      setIsLaunching(false);
    }
  };

  // ── Derived values ────────────────────────────────────────────────────────
  const candidateList  = candidates.split('\n').filter(x => x.includes('@'));
  const requesting     = candidateList.length;
  const isOverQuota    = remaining !== null && requesting > remaining;
  const quotaExhausted = remaining !== null && remaining === 0;

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div className="flex-1 h-[calc(100vh-5rem)] bg-slate-50 dark:bg-[#05050A] text-slate-900 dark:text-slate-200 font-sans flex flex-col overflow-hidden transition-colors duration-500 relative">
      <style>{customStyles}</style>

      {/* Ambient Glows */}
      <div className="absolute top-[-10%] left-[10%] w-[50%] h-[50%] rounded-full bg-indigo-200/40 dark:bg-indigo-600/10 blur-[120px] animate-blob z-0 pointer-events-none" />
      <div className="absolute bottom-[10%] right-[-10%] w-[50%] h-[50%] rounded-full bg-violet-200/40 dark:bg-violet-600/10 blur-[120px] animate-blob z-0 pointer-events-none" style={{ animationDelay: '2s' }} />

      <div className="px-6 lg:px-12 py-8 max-w-[1400px] mx-auto w-full flex flex-col flex-1 min-h-0 relative z-10">

        {/* ── Quota exhausted banner ───────────────────────────────────── */}
        {quotaExhausted && (
          <div className="mb-4 bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/20 rounded-2xl px-6 py-4 flex items-center justify-between gap-4 shrink-0">
            <div className="flex items-center gap-3">
              <span className="text-xl">⚡</span>
              <div>
                <p className="text-sm font-semibold text-amber-900 dark:text-amber-300">
                  You have no interviews remaining
                </p>
                <p className="text-xs text-amber-700 dark:text-amber-400 font-light mt-0.5">
                  Buy more to continue scheduling
                </p>
              </div>
            </div>
            <button
              onClick={() => setUpgradeModal('growth')}
              className="shrink-0 bg-amber-500 hover:bg-amber-400 text-white px-5 py-2 rounded-xl text-xs font-bold tracking-wide transition-all"
            >
              Buy More
            </button>
          </div>
        )}

        {/* ── Stepper Header ───────────────────────────────────────────── */}
        <div className="flex justify-between items-center bg-white dark:bg-[#0a0a0a]/50 backdrop-blur-md p-5 rounded-[1.5rem] border border-slate-200 dark:border-white/10 shadow-sm dark:shadow-none mb-6 shrink-0">
          {['Select JD', 'Add Candidates', 'Configure', 'Launch'].map((label, idx) => {
            const stepNum     = idx + 1;
            const isActive    = step === stepNum;
            const isCompleted = step > stepNum;
            return (
              <div key={idx} className="flex items-center gap-3 px-2">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm transition-colors
                  ${isActive    ? 'bg-indigo-600 text-white shadow-md shadow-indigo-500/30 dark:shadow-[0_0_15px_rgba(99,102,241,0.4)]'
                  : isCompleted ? 'bg-emerald-500 text-white'
                  : 'bg-slate-100 dark:bg-white/5 text-slate-400 dark:text-slate-500 border border-transparent dark:border-white/10'}`}>
                  {isCompleted ? <Check size={18} /> : stepNum}
                </div>
                <span className={`font-semibold hidden sm:block tracking-wide text-sm ${isActive ? 'text-indigo-700 dark:text-indigo-400' : 'text-slate-500 dark:text-slate-500'}`}>
                  {label}
                </span>
                {stepNum !== 4 && <div className="w-10 lg:w-20 h-px bg-slate-200 dark:bg-white/10 mx-2 hidden md:block" />}
              </div>
            );
          })}
        </div>

        {/* ── MAIN WIZARD CARD ─────────────────────────────────────────── */}
        <div className="bg-white dark:bg-[#0a0a0a]/50 backdrop-blur-md rounded-[2rem] border border-slate-200 dark:border-white/10 shadow-sm dark:shadow-none flex flex-col flex-1 min-h-0 overflow-hidden">

          {/* ── STEP 1: SELECT JD ────────────────────────────────────────── */}
          {step === 1 && (
            <>
              <div className="px-8 py-6 border-b border-slate-200 dark:border-white/10 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 shrink-0">
                <h2 className="text-xl font-semibold tracking-wide text-slate-900 dark:text-white">1. Choose Job Description</h2>
                <div className="flex flex-col sm:flex-row items-center gap-4 w-full sm:w-auto">
                  <div className="relative w-full sm:w-64 group">
                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500 group-focus-within:text-indigo-500 transition-colors" size={16} />
                    <input
                      type="text"
                      placeholder="Search descriptions..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full pl-11 pr-4 py-2.5 bg-white dark:bg-black/50 border border-slate-200 dark:border-white/10 rounded-xl text-sm placeholder:text-[12px] text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500/50 transition-all shadow-sm dark:shadow-none"
                    />
                  </div>
                  <button
                    onClick={() => setShowJDModal(true)}
                    className="w-full sm:w-auto text-indigo-600 dark:text-indigo-400 text-sm placeholder:text-[12px] tracking-wide font-bold hover:bg-indigo-50 dark:hover:bg-indigo-500/10 px-5 py-2.5 rounded-xl transition-colors flex items-center justify-center gap-2 border border-transparent dark:hover:border-indigo-500/20 whitespace-nowrap"
                  >
                    <Plus size={16} strokeWidth={2} /> Create New
                  </button>
                </div>
              </div>

              <div className="p-8 flex-1 overflow-y-auto bg-slate-50/50 dark:bg-transparent custom-scrollbar">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                  {filteredJDs.map(jd => (
                    <div
                      key={jd.id}
                      onClick={() => setSelectedJD(jd)}
                      className={`p-6 border rounded-2xl cursor-pointer transition-all ${
                        selectedJD?.id === jd.id
                          ? 'border-indigo-500 bg-indigo-50/50 dark:bg-indigo-500/10 shadow-sm dark:shadow-[0_0_15px_rgba(99,102,241,0.15)]'
                          : 'border-slate-200 dark:border-white/10 hover:border-indigo-300 dark:hover:border-white/20 bg-white dark:bg-white/5'
                      }`}
                    >
                      <h3 className="font-medium dark:font-medium text-slate-900 dark:text-white text-lg mb-3 line-clamp-1">{jd.title}</h3>
                      <div className="flex flex-wrap gap-2">
                        {jd.skills.split(',').slice(0, 3).map((s, i) => (
                          <span key={i} className="text-[12px] font-bold tracking-wide bg-slate-100 dark:bg-white/5 border border-slate-200 dark:border-white/10 text-slate-600 dark:text-slate-400 px-3 py-1.5 rounded-lg">
                            {s.trim()}
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                  {filteredJDs.length === 0 && (
                    <div className="col-span-full text-center py-20 text-slate-400 dark:text-slate-500 font-light tracking-wide">
                      No results found. Adjust your search or create a new description.
                    </div>
                  )}
                </div>
              </div>

              <div className="px-8 py-5 border-t border-slate-200 dark:border-white/10 bg-white dark:bg-[#0a0a0a] flex justify-end shrink-0">
                <button
                  disabled={!selectedJD}
                  onClick={() => setStep(2)}
                  className="bg-indigo-600 hover:bg-indigo-700 dark:hover:bg-indigo-500 text-white px-8 py-3.5 rounded-xl text-xs font-bold tracking-wide disabled:opacity-50 transition-all shadow-md dark:shadow-[0_0_15px_rgba(99,102,241,0.3)] active:scale-95"
                >
                  Next: Candidates
                </button>
              </div>
            </>
          )}

          {/* ── STEP 2: CANDIDATES ───────────────────────────────────────── */}
          {step === 2 && (
            <>
              <div className="px-8 py-6 border-b border-slate-200 dark:border-white/10 shrink-0">
                <h2 className="text-xl font-semibold tracking-wide text-slate-900 dark:text-white">2. Add Candidates</h2>
              </div>

              <div className="p-8 flex-1 flex flex-col overflow-y-auto bg-slate-50/50 dark:bg-transparent custom-scrollbar">
                <div className="border border-dashed border-indigo-200 dark:border-indigo-500/30 rounded-3xl p-8 text-center bg-indigo-50/30 dark:bg-indigo-500/5 mb-6 shrink-0">
                  <UserPlus className="mx-auto text-indigo-400 dark:text-indigo-400/80 mb-3" size={32} strokeWidth={1.5} />
                  <p className="text-indigo-900 dark:text-indigo-300 font-light text-lg mb-1 tracking-wide">Drag & Drop CSV/XLSX here</p>
                  <p className="text-xs text-indigo-500 dark:text-indigo-400/60 tracking-wide font-semibold mt-2">or paste emails directly below</p>
                </div>

                <div className="flex-1 flex flex-col min-h-[200px]">
                  <textarea
                    className="w-full h-full bg-white dark:bg-black/50 border border-slate-200 dark:border-white/10 rounded-2xl p-6 font-mono text-sm placeholder:text-[12px] text-slate-900 dark:text-white focus:ring-1 focus:ring-indigo-500/50 focus:border-indigo-500 outline-none transition-all resize-none shadow-sm dark:shadow-none placeholder-slate-400 dark:placeholder-slate-600"
                    placeholder={"candidate1@example.com\ncandidate2@example.com"}
                    value={candidates}
                    onChange={(e) => setCandidates(e.target.value)}
                  />
                </div>

                {/* Live count + quota warning */}
                {remaining !== null && requesting > 0 && (
                  <div className={`mt-4 flex items-center justify-between text-xs px-4 py-3 rounded-xl border ${
                    isOverQuota
                      ? 'bg-rose-50 dark:bg-rose-500/10 border-rose-200 dark:border-rose-500/20 text-rose-600 dark:text-rose-400'
                      : 'bg-slate-50 dark:bg-white/5 border-slate-200 dark:border-white/10 text-slate-500 dark:text-slate-400'
                  }`}>
                    <span>
                      {requesting} candidate{requesting !== 1 ? 's' : ''} detected
                      {isOverQuota && ` — ${requesting - remaining} over your balance`}
                    </span>
                    <span className="font-bold">{remaining} remaining</span>
                  </div>
                )}
              </div>

              <div className="px-8 py-5 border-t border-slate-200 dark:border-white/10 bg-white dark:bg-[#0a0a0a] flex justify-between items-center shrink-0">
                <button onClick={() => setStep(1)} className="text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white text-xs font-bold tracking-wide px-6 py-3 rounded-xl transition-colors">Back</button>
                <button
                  disabled={!candidates || isOverQuota}
                  onClick={() => setStep(3)}
                  className="bg-indigo-600 hover:bg-indigo-700 dark:hover:bg-indigo-500 text-white px-8 py-3.5 rounded-xl text-xs font-bold tracking-wide disabled:opacity-50 transition-all shadow-md dark:shadow-[0_0_15px_rgba(99,102,241,0.3)] active:scale-95"
                >
                  Next: Configure
                </button>
              </div>
            </>
          )}

          {/* ── STEP 3: CONFIGURE ────────────────────────────────────────── */}
          {step === 3 && (
            <>
              <div className="px-8 py-6 border-b border-slate-200 dark:border-white/10 shrink-0">
                <h2 className="text-xl font-semibold tracking-wide text-slate-900 dark:text-white">3. Interview Setup</h2>
              </div>

              <div className="p-8 flex-1 overflow-y-auto bg-slate-50/50 dark:bg-transparent custom-scrollbar space-y-10">

                {/* Interview Mode */}
                <div>
                  <label className="block text-[12px] font-semibold text-slate-500 dark:text-slate-400 tracking-wide mb-4">Interview Mode</label>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div
                      onClick={() => setIsCustom(false)}
                      className={`p-6 rounded-2xl border cursor-pointer transition-all hover:-translate-y-0.5 ${
                        !isCustom
                          ? 'border-indigo-500 bg-indigo-50/50 dark:bg-indigo-500/10 shadow-sm dark:shadow-[0_0_15px_rgba(99,102,241,0.15)]'
                          : 'border-slate-200 dark:border-white/10 hover:border-indigo-300 dark:hover:border-white/20 bg-white dark:bg-white/5'
                      }`}
                    >
                      <div className="flex items-center gap-4 mb-4">
                        <div className={`w-10 h-10 flex items-center justify-center rounded-xl border ${!isCustom ? 'bg-indigo-600 border-indigo-500 text-white' : 'bg-slate-100 dark:bg-black/40 border-slate-200 dark:border-white/10 text-slate-500 dark:text-slate-400'}`}>
                          <Bot size={20} strokeWidth={1.5} />
                        </div>
                        <h3 className={`font-medium text-lg tracking-wide ${!isCustom ? 'text-indigo-900 dark:text-indigo-300' : 'text-slate-700 dark:text-white'}`}>AI Dynamic Interview</h3>
                      </div>
                      <p className={`text-xs leading-relaxed font-light ${!isCustom ? 'text-indigo-700/80 dark:text-indigo-200/60' : 'text-slate-500 dark:text-slate-400'}`}>
                        The AI agent naturally converses, adapts, and asks probing questions dynamically based on the Job Description.
                      </p>
                    </div>

                    <div
                      onClick={() => setIsCustom(true)}
                      className={`p-6 rounded-2xl border cursor-pointer transition-all hover:-translate-y-0.5 ${
                        isCustom
                          ? 'border-indigo-500 bg-indigo-50/50 dark:bg-indigo-500/10 shadow-sm dark:shadow-[0_0_15px_rgba(99,102,241,0.15)]'
                          : 'border-slate-200 dark:border-white/10 hover:border-indigo-300 dark:hover:border-white/20 bg-white dark:bg-white/5'
                      }`}
                    >
                      <div className="flex items-center gap-4 mb-4">
                        <div className={`w-10 h-10 flex items-center justify-center rounded-xl border ${isCustom ? 'bg-indigo-600 border-indigo-500 text-white' : 'bg-slate-100 dark:bg-black/40 border-slate-200 dark:border-white/10 text-slate-500 dark:text-slate-400'}`}>
                          <ListChecks size={20} strokeWidth={1.5} />
                        </div>
                        <h3 className={`font-medium text-lg tracking-wide ${isCustom ? 'text-indigo-900 dark:text-indigo-300' : 'text-slate-700 dark:text-white'}`}>Custom Hardcoded</h3>
                      </div>
                      <p className={`text-xs leading-relaxed font-light ${isCustom ? 'text-indigo-700/80 dark:text-indigo-200/60' : 'text-slate-500 dark:text-slate-400'}`}>
                        Provide a strict list of specific questions. The AI will read them exactly as written and evaluate.
                      </p>
                    </div>
                  </div>
                </div>

                {/* AI focus selector */}
                {!isCustom && (
                  <div className="animate-in fade-in slide-in-from-bottom-2 duration-300">
                    <label className="block text-[12px] font-semibold text-slate-500 dark:text-slate-400 tracking-wide mb-4">Focus Area (AI Agent)</label>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {['Code Only', 'Behavioral Only', 'Hybrid'].map(type => (
                        <button
                          key={type}
                          onClick={() => setInterviewType(type)}
                          className={`p-4 border rounded-xl font-bold text-xs tracking-wide transition-all ${
                            interviewType === type
                              ? 'bg-indigo-600 text-white border-indigo-500 shadow-md dark:shadow-[0_0_15px_rgba(99,102,241,0.3)]'
                              : 'text-slate-600 dark:text-slate-400 border-slate-200 dark:border-white/10 hover:border-slate-400 dark:hover:border-white/30 bg-white dark:bg-black/40'
                          }`}
                        >
                          {type}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Custom question builder */}
                {isCustom && (
                  <div className="bg-slate-100/50 dark:bg-white/5 p-8 rounded-[2rem] border border-slate-200 dark:border-white/10 animate-in fade-in slide-in-from-bottom-2 duration-300">
                    <div className="flex justify-between items-center mb-6">
                      <h3 className="text-lg font-medium tracking-wide text-slate-800 dark:text-white">Question Sequence</h3>
                      <span className="text-[12px] font-bold tracking-wide bg-indigo-100 dark:bg-indigo-500/20 text-indigo-700 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-500/30 px-3 py-1.5 rounded-lg">
                        {customQuestions.length} Questions
                      </span>
                    </div>

                    <div className="space-y-4">
                      {customQuestions.map((q, idx) => (
                        <div key={q.id} className="flex gap-4 items-start bg-white dark:bg-[#111] p-5 rounded-2xl border border-slate-200 dark:border-white/10 shadow-sm dark:shadow-none group hover:border-indigo-300 dark:hover:border-indigo-500/50 transition-colors">
                          <div className="flex flex-col gap-1 pt-1 opacity-30 group-hover:opacity-100 transition-opacity">
                            <button onClick={() => handleMoveQuestion(idx, -1)} disabled={idx === 0} className="hover:text-indigo-600 dark:hover:text-indigo-400 disabled:invisible p-1 rounded hover:bg-indigo-50 dark:hover:bg-indigo-500/10">
                              <ArrowUp size={16} />
                            </button>
                            <button onClick={() => handleMoveQuestion(idx, 1)} disabled={idx === customQuestions.length - 1} className="hover:text-indigo-600 dark:hover:text-indigo-400 disabled:invisible p-1 rounded hover:bg-indigo-50 dark:hover:bg-indigo-500/10">
                              <ArrowDown size={16} />
                            </button>
                          </div>

                          <div className="flex-1">
                            <textarea
                              value={q.text}
                              onChange={(e) => handleUpdateQuestion(q.id, 'text', e.target.value)}
                              placeholder={`Question ${idx + 1}...`}
                              className="w-full text-sm placeholder:text-[12px] font-medium p-4 bg-slate-50 dark:bg-black/50 border border-slate-100 dark:border-white/5 rounded-xl text-slate-900 dark:text-white focus:ring-1 focus:ring-indigo-500/50 focus:border-indigo-500 outline-none resize-none h-24 transition-all placeholder-slate-400 dark:placeholder-slate-600"
                            />
                          </div>

                          <div className="flex flex-col gap-3 shrink-0 w-36">
                            <select
                              value={q.type}
                              onChange={(e) => handleUpdateQuestion(q.id, 'type', e.target.value)}
                              className="w-full text-[12px] font-bold tracking-wide text-slate-700 dark:text-slate-300 p-3 border border-slate-200 dark:border-white/10 rounded-xl bg-slate-50 dark:bg-black/50 focus:ring-1 focus:ring-indigo-500/50 focus:border-indigo-500 outline-none cursor-pointer transition-all"
                            >
                              <option value="verbal" className="bg-white dark:bg-[#111]">Verbal Chat</option>
                              <option value="coding" className="bg-white dark:bg-[#111]">Coding Task</option>
                            </select>

                            <button
                              onClick={() => handleRemoveQuestion(q.id)}
                              className="flex items-center justify-center gap-1.5 text-[12px] tracking-wide font-bold text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-500/10 py-3 rounded-xl transition-colors border border-transparent dark:hover:border-rose-500/20"
                            >
                              <Trash2 size={14} /> Remove
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>

                    <button
                      onClick={handleAddQuestion}
                      className="mt-6 w-full py-4 border border-dashed border-indigo-200 dark:border-indigo-500/30 rounded-2xl text-indigo-600 dark:text-indigo-400 text-xs font-bold tracking-wide hover:bg-indigo-50 dark:hover:bg-indigo-500/10 flex items-center justify-center gap-2 transition-all active:scale-[0.99]"
                    >
                      <PlusCircle size={18} strokeWidth={1.5} /> Add Another Question
                    </button>
                  </div>
                )}

                {/* Email template */}
                <div>
                  <label className="block text-[12px] font-semibold text-slate-500 dark:text-slate-400 tracking-wide mb-4 flex items-center gap-2">
                    <Mail size={14} className="text-indigo-400" /> Email Invite Template
                  </label>
                  <textarea
                    className="w-full bg-white dark:bg-black/50 border border-slate-200 dark:border-white/10 rounded-3xl p-6 h-40 font-medium text-sm placeholder:text-[12px] text-slate-700 dark:text-white focus:ring-1 focus:ring-indigo-500/50 focus:border-indigo-500 outline-none transition-all resize-none leading-relaxed shadow-sm dark:shadow-none"
                    value={emailBody}
                    onChange={(e) => setEmailBody(e.target.value)}
                  />
                </div>
              </div>

              <div className="px-8 py-5 border-t border-slate-200 dark:border-white/10 bg-white dark:bg-[#0a0a0a] flex justify-between items-center shrink-0">
                <button onClick={() => setStep(2)} className="text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white text-xs font-bold tracking-wide px-6 py-3 rounded-xl transition-colors">Back</button>
                <button
                  onClick={validateStep3}
                  className="bg-indigo-600 hover:bg-indigo-700 dark:hover:bg-indigo-500 text-white px-8 py-3.5 rounded-xl text-xs font-bold tracking-wide transition-all shadow-md dark:shadow-[0_0_15px_rgba(99,102,241,0.3)] active:scale-95"
                >
                  Next: Review
                </button>
              </div>
            </>
          )}

          {/* ── STEP 4: LAUNCH ───────────────────────────────────────────── */}
          {step === 4 && (
            <div className="flex-1 bg-white dark:bg-transparent p-12 flex flex-col items-center justify-center text-center overflow-y-auto custom-scrollbar animate-in zoom-in-95 duration-300">
              <div className="bg-indigo-50 dark:bg-indigo-500/10 p-8 rounded-full mb-8 border border-indigo-100 dark:border-indigo-500/20 shadow-sm dark:shadow-[0_0_30px_rgba(99,102,241,0.2)]">
                <Rocket size={48} className="text-indigo-600 dark:text-indigo-400" strokeWidth={1.5} />
              </div>

              <h2 className="text-4xl font-semibold tracking-wide text-slate-900 dark:text-white mb-4">Ready to Launch?</h2>

              <p className="text-slate-500 dark:text-slate-400 max-w-lg mb-6 text-sm font-light leading-relaxed">
                You are about to invite{' '}
                <strong className="font-semibold text-slate-900 dark:text-white">
                  {requesting} candidate{requesting !== 1 ? 's' : ''}
                </strong>{' '}
                for the{' '}
                <strong className="font-semibold text-slate-900 dark:text-white">{selectedJD?.title}</strong> role.
                <br /><br />
                <span className="inline-block bg-indigo-50 dark:bg-indigo-500/10 text-indigo-700 dark:text-indigo-300 px-4 py-1.5 rounded-lg text-[12px] tracking-wide font-bold border border-indigo-200 dark:border-indigo-500/20">
                  Mode: {isCustom ? `Custom (${customQuestions.length} Questions)` : `AI Dynamic (${interviewType})`}
                </span>
              </p>

              {/* Remaining interviews counter */}
              {remaining !== null && (
                <div className={`w-full max-w-md mb-8 rounded-2xl p-5 text-center border ${
                  isOverQuota
                    ? 'bg-rose-50 dark:bg-rose-500/10 border-rose-200 dark:border-rose-500/20'
                    : 'bg-white dark:bg-white/5 border-slate-200 dark:border-white/10'
                }`}>
                  <span className={`text-4xl font-bold ${isOverQuota ? 'text-rose-500' : 'text-slate-900 dark:text-white'}`}>
                    {remaining}
                  </span>
                  <p className="text-xs text-slate-400 mt-1 tracking-wide font-bold">
                    interviews remaining
                  </p>

                  {isOverQuota && (
                    <div className="mt-4">
                      <p className="text-[12px] text-rose-600 dark:text-rose-400 font-medium mb-3">
                        You're inviting <strong>{requesting}</strong> but only have <strong>{remaining}</strong> remaining.
                      </p>
                      <button
                        onClick={() => setUpgradeModal('growth')}
                        className="text-xs font-bold tracking-wide text-indigo-600 dark:text-indigo-400 hover:underline"
                      >
                        Buy more interviews →
                      </button>
                    </div>
                  )}
                </div>
              )}

              <div className="flex gap-4 w-full max-w-md shrink-0">
                <button
                  disabled={isLaunching}
                  onClick={() => setStep(3)}
                  className="flex-1 text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white bg-slate-100 dark:bg-white/5 border border-transparent dark:border-white/10 dark:hover:bg-white/10 px-6 py-4 rounded-2xl text-xs font-bold tracking-wide transition-colors"
                >
                  Back
                </button>
                <button
                  onClick={handleLaunch}
                  disabled={isLaunching || isOverQuota || quotaExhausted}
                  className="flex-[2] bg-indigo-600 hover:bg-indigo-500 text-white px-8 py-4 rounded-2xl font-bold text-xs tracking-wide shadow-md dark:shadow-[0_0_20px_rgba(99,102,241,0.4)] flex items-center justify-center gap-3 disabled:opacity-50 transition-all active:scale-95"
                >
                  {isLaunching
                    ? <><Loader2 className="animate-spin" size={18} /> Launching...</>
                    : 'Launch Campaign'
                  }
                </button>
              </div>

              {quotaExhausted && (
                <p className="mt-5 text-xs text-slate-400">
                  No interviews remaining.{' '}
                  <button
                    onClick={() => setUpgradeModal('growth')}
                    className="text-indigo-600 dark:text-indigo-400 font-bold hover:underline"
                  >
                    Buy more interviews
                  </button>{' '}
                  to continue.
                </p>
              )}
            </div>
          )}

        </div>{/* end wizard card */}

        {/* JD creation modal */}
        {showJDModal && (
          <JDModal
            onClose={() => setShowJDModal(false)}
            onSuccess={(newJD) => { fetchJDs(); setSelectedJD(newJD); }}
          />
        )}

      </div>

      {/* Upgrade / Buy More Modal */}
      {upgradeModal && (
        <UpgradeModal
          plan={upgradeModal}
          onClose={() => setUpgradeModal(null)}
          onSuccess={() => {
            setUpgradeModal(null);
            fetchQuota();
            window.dispatchEvent(new Event('app-refresh'));
          }}
        />
      )}

    </div>
  );
};

export default Schedule;